import moment from "moment";
import { compose } from "redux";
import classNames from "classnames";
import debounce from "lodash/debounce";
import React, { PureComponent } from "react";
import { Fields } from "redux-form/immutable";
import { reduxForm } from "redux-form/immutable";
import ChevronLeft from "@material-ui/icons/ChevronLeft";
import ChevronRight from "@material-ui/icons/ChevronRight";
import { Grid, Typography, ButtonBase, withStyles } from "@material-ui/core";

import { BasePageTitle } from "altus-ui-components";

import {
  WORK_SCHEDULE_REPORT_FORM,
  WORK_SCHEDULE_REPORT_TYPE,
} from "features/planner/report/report.constants";

import { workScheduleReportFormOnChange } from "features/planner/report/report.actions";

const WorkScheduleReportHeaderTitle = ({ classes, children }) => (
  <Grid
    item
    xs
    noWrap
    variant="subtitle2"
    component={Typography}
    className={classes.headerTitle}
  >
    {children}
  </Grid>
);

const WorkScheduleReportHeaderIcon = ({ classes, component }) => (
  <Grid
    item
    fontSize="small"
    component={component}
    className={classes.headerIcon}
  />
);

const WorkScheduleReportHeaderYearField = ({
  classes,
  yearNumber,
  isFirstYear,
  isLastYear,
  ...rest
}) => {
  const { input: inputYear } = rest[WORK_SCHEDULE_REPORT_FORM.YEAR];
  const { input: inputReportType } = rest[WORK_SCHEDULE_REPORT_FORM.TYPE];

  const reportType = inputReportType.value;

  return (
    <Grid
      item
      container
      justifyContent="center"
      alignItems="center"
      component={ButtonBase}
      onClick={() => inputYear.onChange(yearNumber)}
      className={classNames(classes.headerItem, {
        [classes.headerItemDisabled]:
          inputReportType.value === WORK_SCHEDULE_REPORT_TYPE.MONTH ||
          inputYear.value === yearNumber,
      })}
    >
      {reportType === WORK_SCHEDULE_REPORT_TYPE.YEAR && isFirstYear && (
        <WorkScheduleReportHeaderIcon
          classes={classes}
          component={ChevronLeft}
        />
      )}
      <WorkScheduleReportHeaderTitle classes={classes}>
        {yearNumber}
      </WorkScheduleReportHeaderTitle>
      {reportType === WORK_SCHEDULE_REPORT_TYPE.YEAR && isLastYear && (
        <WorkScheduleReportHeaderIcon
          classes={classes}
          component={ChevronRight}
        />
      )}
    </Grid>
  );
};

const WorkScheduleYearReportHeaderYearMonthTypeField = ({
  classes,
  monthNumber,
  yearNumber,
  isFirstMonth,
  isLastMonth,
  ...rest
}) => {
  const { input: inputMonth } = rest[WORK_SCHEDULE_REPORT_FORM.MONTH];
  const { input: inputYear } = rest[WORK_SCHEDULE_REPORT_FORM.YEAR];
  const { input: inputReportType } = rest[WORK_SCHEDULE_REPORT_FORM.TYPE];

  const reportType = inputReportType.value;

  return (
    <Grid
      item
      container
      justifyContent="center"
      alignItems="center"
      component={ButtonBase}
      className={classNames(classes.headerItem, {
        [classes.headerItemDisabled]:
          reportType === WORK_SCHEDULE_REPORT_TYPE.MONTH &&
          monthNumber === inputMonth.value,
      })}
      onClick={() => {
        inputMonth.onChange(monthNumber);
        inputYear.onChange(yearNumber);
        inputReportType.onChange(WORK_SCHEDULE_REPORT_TYPE.MONTH);
      }}
    >
      {reportType === WORK_SCHEDULE_REPORT_TYPE.MONTH && isFirstMonth && (
        <WorkScheduleReportHeaderIcon
          classes={classes}
          component={ChevronLeft}
        />
      )}
      <WorkScheduleReportHeaderTitle classes={classes}>
        {moment.months(monthNumber).toUpperCase()}
      </WorkScheduleReportHeaderTitle>
      {reportType === WORK_SCHEDULE_REPORT_TYPE.MONTH && isLastMonth && (
        <WorkScheduleReportHeaderIcon
          classes={classes}
          component={ChevronRight}
        />
      )}
    </Grid>
  );
};

class WorkScheduleReportHeaderBase extends PureComponent {
  render() {
    const { dates, renderDate, classes, title } = this.props;

    const datesByYear = dates.groupBy(date => date.year());

    const datesByYearAndMonth = datesByYear.map(dby =>
      dby.groupBy(date => date.month()),
    );

    return (
      <Grid container className={classes.root}>
        <Grid
          item
          xs={2}
          container
          justifyContent="center"
          direction="column"
          className={classes.titleContainer}
        >
          <BasePageTitle title={title} />
        </Grid>
        <Grid item xs container className={classes.monthReportHeaderRoot}>
          {datesByYearAndMonth.keySeq().map((year, yearIndex) => {
            const datesByMonth = datesByYearAndMonth.get(year);
            const datesInYear = datesByYear.get(year);

            return (
              <Grid
                container
                key={year}
                className={classes.monthReportYearContainer}
                style={{
                  width: `${(datesInYear.size / dates.size) * 100}%`,
                }}
              >
                <Fields
                  yearNumber={year}
                  classes={classes}
                  component={WorkScheduleReportHeaderYearField}
                  isFirstYear={yearIndex === 0}
                  isLastYear={yearIndex === datesByYearAndMonth.size - 1}
                  names={[
                    WORK_SCHEDULE_REPORT_FORM.YEAR,
                    WORK_SCHEDULE_REPORT_FORM.TYPE,
                  ]}
                />
                <Grid item container wrap="nowrap">
                  {datesByMonth.keySeq().map((month, monthIndex) => {
                    const datesInMonth = datesByMonth.get(month);

                    return (
                      <Grid
                        item
                        container
                        key={month}
                        className={classes.monthReportMonthContainer}
                        style={{
                          width: `${(datesInMonth.size / datesInYear.size) *
                            100}%`,
                        }}
                      >
                        <Fields
                          yearNumber={year}
                          classes={classes}
                          monthNumber={month}
                          isLastMonth={
                            monthIndex === datesByMonth.size - 1 &&
                            yearIndex === datesByYearAndMonth.size - 1
                          }
                          isFirstMonth={monthIndex === 0 && yearIndex === 0}
                          component={
                            WorkScheduleYearReportHeaderYearMonthTypeField
                          }
                          names={[
                            WORK_SCHEDULE_REPORT_FORM.YEAR,
                            WORK_SCHEDULE_REPORT_FORM.MONTH,
                            WORK_SCHEDULE_REPORT_FORM.TYPE,
                          ]}
                        />

                        <Grid item container wrap="nowrap">
                          {datesInMonth.map(renderDate)}
                        </Grid>
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
    );
  }
}

const styles = theme => ({
  headerTitle: {
    fontSize: theme.typography.caption.fontSize,
  },
  monthReportHeaderRoot: {
    borderLeft: "none",
    borderTop: theme.altus.workScheduleReport.defaultBorder,
    borderBottom: theme.altus.workScheduleReport.defaultBorder,
  },
  monthReportHeaderDayRow: {
    borderLeft: theme.altus.workScheduleReport.defaultBorder,
  },
  monthReportYearContainer: {
    borderLeft: theme.altus.workScheduleReport.defaultBorder,
  },
  monthReportMonthContainer: {
    "&:not(:first-child)": {
      borderLeft: theme.altus.workScheduleReport.defaultBorder,
    },
  },
  headerItem: {
    borderBottom: theme.altus.workScheduleReport.defaultBorder,
    height: theme.altus.row.height,
    padding: `0 ${theme.spacing(1)}px`,
    "&:hover": {
      background: theme.palette.action.hover,
    },
  },
  headerItemDisabled: {
    pointerEvents: "none",
  },
  headerIcon: {
    display: "flex",
  },
  root: {
    top: 0,
    zIndex: 3,
    position: "sticky",
    background: theme.palette.background.default,
  },
  titleContainer: {
    padding: theme.spacing(1),
  },
});

export default compose(
  reduxForm({
    destroyOnUnmount: false,
    form: WORK_SCHEDULE_REPORT_FORM.ID,
    onChange: debounce(workScheduleReportFormOnChange, 300),
  }),
  withStyles(styles),
)(WorkScheduleReportHeaderBase);
